import React, { useEffect, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Grid2 from "@mui/material/Grid2";
import Alert from "../layout/Alert";
import ResponsiveContentWrapper from "../layout/ResponsiveContentWrapper";
import env from "../../env";
import UserInfoWrapper from "../Common/user/UserInfoWrapper";
import { generateSecondaryOtp, validateSecondaryOtp } from "../../action/otp";
import { SectionHeading, LabelText, ValueText } from "../Common/Typography/AppTypography";
import Dialog from "../Common/Dailog/Dailog";
import FormInput from "../Common/FormInputs/MuiTextField";

const Profile = ({ wrapperClass }) => {
  const [fetchedUser, setFetchedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [openVerifyModal, setOpenVerifyModal] = useState(false);
  const [emailToVerify, setEmailToVerify] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpMessage, setOtpMessage] = useState("");

  useEffect(() => {
    const fetchTokenDetails = async () => {
      try {
        setLoading(true);
        const umURL = env.GET_USERMANAGEMENT_API_URL();
        const config = {
          headers: {
            "Content-Type": "application/json",
          },
        };
        const res = await axios.get(`${umURL}/simplifi/v1/userManagement/tokenDetails`, config);
        setFetchedUser(res.data);
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Failed to fetch user");
      } finally {
        setLoading(false);
      }
    };
    fetchTokenDetails();
  }, []);

  const formatUsernameToName = (username) => {
    if (!username) return "";
    const parts = username.split(/[._\s]+/).filter(Boolean);
    return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ").trim();
  };

  const formatNameParts = (firstName, lastName) => {
    const f = firstName ? firstName.trim() : "";
    const l = lastName ? lastName.trim() : "";
    const parts = [];
    if (f) parts.push(f.charAt(0).toUpperCase() + f.slice(1).toLowerCase());
    if (l) parts.push(l.charAt(0).toUpperCase() + l.slice(1).toLowerCase());
    return parts.join(" ").trim();
  };

  const extractErrorMessage = (err) => {
    if (!err) return "Unknown error";
    if (typeof err === "string") return err;
    if (err.response && err.response.data) {
      const d = err.response.data;
      if (typeof d === "string") return d;
      if (d.message) return d.message;
      if (d.error) return d.error;
      try {
        return JSON.stringify(d);
      } catch (e) {
        return String(d);
      }
    }
    return err.message || String(err);
  };

  const handleSendOtp = async () => {
    if (!emailToVerify) return setOtpMessage("Please enter a valid email");
    try {
      setOtpLoading(true);
      setOtpMessage("");
      await generateSecondaryOtp(emailToVerify);
      setOtpSent(true);
      setOtpMessage("OTP sent to your email successfully.");
    } catch (err) {
      setOtpMessage(extractErrorMessage(err));
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) return setOtpMessage("Please enter the OTP");
    try {
      setOtpLoading(true);
      setOtpMessage("");
      await validateSecondaryOtp(emailToVerify, otp);
      setOtpMessage("Email verified successfully!");
      setOtpSent(false);
      setTimeout(() => setOpenVerifyModal(false), 1500);
    } catch (err) {
      setOtpMessage(extractErrorMessage(err));
    } finally {
      setOtpLoading(false);
    }
  };

  const closeVerifyModal = () => {
    setOpenVerifyModal(false);
    setOtpSent(false);
    setOtp("");
    setOtpMessage("");
  };

  const renderUserRow = (user) => (
    <Grid2 container spacing={2} sx={{ mt: 1 }}>
      <Grid2 size={{ xs: 12, md: 4 }}>
        <Box sx={{ minWidth: 0 }}>
          <LabelText sx={{ color: "text.secondary", mb: 0.5 }}>Name</LabelText>
          <ValueText sx={{ fontWeight: 500 }}>
            {user.username
              ? formatUsernameToName(user.username)
              : formatNameParts(user.firstName, user.lastName)}
          </ValueText>
        </Box>
      </Grid2>
      <Grid2 size={{ xs: 12, md: 4 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
            <LabelText sx={{ color: "text.secondary" }}>Email ID</LabelText>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => {
                setEmailToVerify(user.email);
                setOtpSent(false);
                setOtp("");
                setOtpMessage("");
                setOpenVerifyModal(true);
              }}
              sx={{ fontSize: 12, verticalAlign: "baseline" }}
            >
              Verify
            </Link>
          </Box>
          <ValueText sx={{ fontWeight: 500, wordBreak: "break-all" }}>{user.email}</ValueText>
        </Box>
      </Grid2>
      <Grid2 size={{ xs: 12, md: 4 }}>
        <Box sx={{ minWidth: 0 }}>
          <LabelText sx={{ color: "text.secondary", mb: 0.5 }}>Mobile</LabelText>
          <ValueText sx={{ fontWeight: 500 }}>{user.mobile || "-"}</ValueText>
        </Box>
      </Grid2>
    </Grid2>
  );

  return (
    <ResponsiveContentWrapper className={wrapperClass || ""}>
      <Box sx={{ width: "100%", minWidth: 0, maxWidth: 900 }}>
        <Paper variant="outlined" sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
          <SectionHeading sx={{ mb: 0 }}>User Information</SectionHeading>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : error ? (
            <ValueText sx={{ color: "error.main", mt: 2 }}>{error}</ValueText>
          ) : fetchedUser ? (
            <Box sx={{ mt: 2 }}>
              <Alert />
              {renderUserRow(fetchedUser)}
            </Box>
          ) : (
            <UserInfoWrapper>
              {({ user, withoutOnboard }) => (
                <Box sx={{ mt: 2 }}>
                  {user ? (
                    renderUserRow(user)
                  ) : (
                    <ValueText color="text.secondary">
                      {withoutOnboard ? "No onboard data available" : "No user data available"}
                    </ValueText>
                  )}
                </Box>
              )}
            </UserInfoWrapper>
          )}
        </Paper>
      </Box>

      <Dialog
        open={openVerifyModal}
        onClose={closeVerifyModal}
        title="Email Verification"
        maxWidth="xs"
        fullWidth
        closeOnBackdropClick
        closeOnEscapeKeyDown
        actions={
          !otpSent
            ? [
                { label: "Cancel", variant: "ghost", onClick: closeVerifyModal },
                {
                  label: otpLoading ? "Sending..." : "Send OTP",
                  variant: "primary",
                  onClick: handleSendOtp,
                  disabled: otpLoading,
                },
              ]
            : [
                { label: "Cancel", variant: "ghost", onClick: closeVerifyModal },
                {
                  label: otpLoading ? "Verifying..." : "Verify OTP",
                  variant: "primary",
                  onClick: handleVerifyOtp,
                  disabled: otpLoading,
                },
              ]
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          <FormInput
            label="Email"
            name="emailVerify"
            value={emailToVerify}
            onChange={(e) => setEmailToVerify(e.target.value)}
            fullWidth
            size="small"
          />
          {otpSent && (
            <FormInput
              label="Enter OTP"
              name="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              fullWidth
              size="small"
            />
          )}
          {otpMessage && (
            <ValueText
              sx={{
                color: otpMessage.includes("success") ? "success.main" : "error.main",
                fontWeight: 500,
              }}
            >
              {otpMessage}
            </ValueText>
          )}
        </Box>
      </Dialog>
    </ResponsiveContentWrapper>
  );
};

export default Profile;
